import { Component, computed, input, InputSignal, OnInit, Signal, signal, WritableSignal } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { UIRouter } from '@uirouter/angular';
import { FeatureFlagsService } from '../../../Common/FeatureFlags/feature-flags-service';
import { InstanceRights, RestrictData, RestrictService, Roles } from '../../../Common/Permissions/restrict.service';
import { AceLoaderComponent } from '../../../UI-Lib/ACE/Loader/loader.component';
import { DirectivesModule } from '../../../UI-Lib/Directives/directives.module';
import { UserInstanceService } from '../../../User/userInstance.service';
import { CompanyService } from '../company.service';
import { RyanInstanceId } from '../../../constants.new';
import { PaymentBatchRepository } from '../../../Core-Repositories/paymentBatch.repository';

@Component({
    selector: 'quick-links',
    templateUrl: './quickLinks.component.html',
    styleUrls: ['./quickLinks.component.scss'],
    imports: [DirectivesModule, AceLoaderComponent],
})
export class QuickLinksComponent implements OnInit {
    constructor(
        private readonly _featureFlagService: FeatureFlagsService,
        private readonly _restrictService: RestrictService,
        private readonly _userInstanceService: UserInstanceService,
        private readonly _companyService: CompanyService,
        private readonly _paymentBatchRepository: PaymentBatchRepository,
        private readonly _routerService: UIRouter,
    ) {}

    company: InputSignal<any> = input();
    parentCompany: InputSignal<Core.ParentCompanyDTO> = input();
    isTopLevelCompany: InputSignal<boolean> = input();

    hasAllocationFeature: boolean;
    hasWritePermission: WritableSignal<boolean> = signal(false);
    isPaymentApprover: WritableSignal<boolean> = signal(false);
    hasPrivateItemsPermission: boolean;
    accrualsEnabled: boolean;

    hasInstancePrivateEditPermission: Signal<boolean> = computed(() => {
        return this._restrictService.hasInstanceRight(InstanceRights.PRIVATEITEMSEDIT, this.company().instanceID);
    });

    hasInstancePrivateViewPermission: Signal<boolean> = computed(() => {
        return (
            this._restrictService.hasInstanceRight(InstanceRights.PRIVATEITEMSEDIT, this.company().instanceID) ||
            this._restrictService.hasInstanceRight(InstanceRights.PRIVATEITEMSVIEW, this.company().instanceID)
        );
    });

    showComplianceInfo: Signal<boolean> = computed(() => {
        return (
            this.company().ppReturnPreparationAllowed &&
            this._restrictService.hasInstanceRight(InstanceRights.COMPLIANCEFEATURESVIEW, this.company().instanceID)
        );
    });

    shouldDisplayPaymentBatches: Signal<boolean> = computed(() => {
        const isRyanUser =
            !this.company().parentId &&
            this.company().instanceID === this._userInstanceService.RyanInstanceId &&
            this._userInstanceService.isCurrentInstanceRyan() &&
            this.hasInstancePrivateEditPermission() &&
            this.hasWritePermission();

        const isApprover = !this.company().parentId && this.isPaymentApprover();

        return isRyanUser || isApprover;
    });

    async ngOnInit(): Promise<void> {
        const hasRyanPrivateItemsView = this._restrictService.isInRole(Roles.RYANPRIVATEITEMSVIEW);
        const hasRyanPrivateItemsEdit = this._restrictService.isInRole(Roles.RYANPRIVATEITEMSEDIT);
        this.hasPrivateItemsPermission =
            (hasRyanPrivateItemsView || hasRyanPrivateItemsEdit) &&
            this._userInstanceService.getSelectedInstance().instanceId === RyanInstanceId;

        this.hasAllocationFeature = this._featureFlagService.featureFlags.enableAllocationFeature;

        const restrictionData = {
            isCompany: true,
            entityId: this.company().companyID,
            roles: [],
            flag: Core.AccessRightsEnum.Write,
        } as RestrictData;

        const dataPromises = [
            this._companyService.checkAccrualsEnabled(),
            this._restrictService.hasPermission(restrictionData),
        ];

        const [accrualsEnabled, hasWritePermission] = await Promise.all(dataPromises);
        this.accrualsEnabled = accrualsEnabled;
        this.hasWritePermission.set(hasWritePermission);

        if (this._featureFlagService.featureFlags.enableClientPaymentApprover && !this.company().parentId) {
            this.isPaymentApprover.set(await lastValueFrom(this._paymentBatchRepository.isApproverForAny()));
        }
    }

    goToPBCC(): void {
        sessionStorage['fromTlCompany'] = this.company().companyName;
        sessionStorage['pbccFilters'] = JSON.stringify({ tlCompanyIds: [this.company().topLevelCompanyId] });
        this._routerService.stateService.go('paymentBatchCommandCenter', null);
    }
}
